import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tabs, Tag } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { FULL_DATE_FORMAT } from 'constants/dates';
import dayjs from 'dayjs';
import { PLAN_TYPE_VALUES } from 'constants/users';
import { usePermission } from 'hooks';
import { PERMISSION_ACTIONS, PERMISSION_ROUTES } from 'constants/permissions';
import { APPOINTMENT_STATUSES } from 'constants/appointments';
import { encodeToBase64 } from '@medicusunion/front-utils';
import {
  fetchAppointmentById,
  openAppointmentRoom
} from 'services/api/appointments';
import { AppointmentDetail } from 'types/api/appointments';

import {
  Details,
  Files,
  Reports,
  Notes,
  RejectModal,
  DateModal,
  PaymentInfo,
  State,
  StateModal,
  StatusSelect
} from './components';
import { AppointmentDetailContext } from './AppointmentDetailContext';
import { Loading, PageWrapper } from 'components/ui';
import { Private } from 'components/shared';
import CallButton from './components/CallButton';
import { showErrorMessage } from 'utils/showErrorMessage';
import { refreshTokenStorage, tokenStorage } from 'utils/storage';

const AppointmentsDetails: FC = () => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const { checkUserPermission } = usePermission();

  const [appointment, setAppointment] = useState<AppointmentDetail | null>(
    null
  );

  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!params.id) return;
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchAppointmentById(params.id as string);
        if (!cancelled) setAppointment(res);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [params.id, refreshKey]);

  const refresh = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  const onOpenRoom = useCallback(async () => {
    if (!params.id) return;

    try {
      const data = await openAppointmentRoom(params.id);
      const url = data?.redirect_url;
      const roomId = data?.meetUrl;
      const token = encodeToBase64(tokenStorage.get());
      const refreshToken = encodeToBase64(refreshTokenStorage.get());

      if (url) {
        const link = `${url}/home/${roomId}?t=${token}&r=${refreshToken}&lang=en`;
        window.location.href = link;
      }
    } catch (err) {
      showErrorMessage(err);
    }
  }, [params.id]);

  const isVideoCall = useMemo(
    () => appointment?.plan?.type === PLAN_TYPE_VALUES.video_call,
    [appointment?.plan?.type]
  );

  const date = useMemo(() => {
    if (!isVideoCall || !appointment?.dates) return '';

    const accepted = appointment.dates.find(item => item.accepted);

    if (accepted) {
      return dayjs(accepted.date).format(FULL_DATE_FORMAT);
    }

    return '';
  }, [appointment?.dates, isVideoCall]);

  const showLogsButton =
    appointment?.status === APPOINTMENT_STATUSES.started ||
    appointment?.status === APPOINTMENT_STATUSES.completed;

  const hasPaymentReadPermission = checkUserPermission(
    PERMISSION_ROUTES.appointments,
    PERMISSION_ACTIONS.appointmentViewPayment
  );

  const hasNotesReadPermission = checkUserPermission(
    PERMISSION_ROUTES.appointmentNote,
    PERMISSION_ACTIONS.read
  );

  const hasReportsReadPermission = checkUserPermission(
    PERMISSION_ROUTES.appointmentReport,
    PERMISSION_ACTIONS.read
  );

  const hasFilesReadPermission = checkUserPermission(
    PERMISSION_ROUTES.appointments,
    PERMISSION_ACTIONS.readFile
  );

  const showJoinCall =
    isVideoCall &&
    appointment?.paid &&
    (appointment?.status === APPOINTMENT_STATUSES.approved ||
      appointment?.status === APPOINTMENT_STATUSES.started);

  if (!appointment) return <Loading />;

  return (
    <AppointmentDetailContext.Provider
      value={{ appointment, loading, refresh }}
    >
      <PageWrapper
        title={t('appointments_details.page_title')}
        onClickBack={() => navigate('/appointments')}
        extra={[
          showJoinCall ? (
            <Private
              route={PERMISSION_ROUTES.super}
              action={PERMISSION_ACTIONS.read}
              key="join-call"
            >
              <CallButton
                appointmentId={params.id as string}
                date={date}
                onJoinClick={onOpenRoom}
              />
            </Private>
          ) : null,
          <Private
            route={PERMISSION_ROUTES.appointments}
            action={PERMISSION_ACTIONS.changeStatus}
            key="status-select"
          >
            <StatusSelect
              appointmentId={params.id as string}
              status={appointment.status}
            />
          </Private>,
          <Private
            route={PERMISSION_ROUTES.super}
            action={PERMISSION_ACTIONS.read}
            key="logs"
          >
            {showLogsButton ? (
              <Button
                href="https://console.twilio.com/us1/monitor/insights/video/video-rooms"
                target="_blank"
                rel="noreferrer"
              >
                View call logs
              </Button>
            ) : null}
          </Private>,
          appointment.paid ? (
            <Tag
              color="success"
              key="paid"
              style={{ height: 32, display: 'flex', alignItems: 'center' }}
            >
              {t('appointments_details.paid')}
            </Tag>
          ) : null
        ]}
        color="white"
        back
      >
        <Tabs
          defaultActiveKey="1"
          style={{ padding: '0 24px' }}
          items={[
            {
              key: '1',
              label: t('appointments_details.appointment_tab_1'),
              style: { paddingBottom: 40 },
              children: <Details date={date} isVideoCall={isVideoCall} />
            },
            ...(hasFilesReadPermission
              ? [
                  {
                    key: '2',
                    label: t('appointments_details.appointment_tab_2'),
                    style: { paddingBottom: 40 },
                    children: <Files />
                  }
                ]
              : []),
            ...(hasReportsReadPermission
              ? [
                  {
                    key: '3',
                    label: t('appointments_details.appointment_tab_3'),
                    style: { paddingBottom: 40 },
                    children: <Reports />
                  }
                ]
              : []),
            ...(hasNotesReadPermission
              ? [
                  {
                    key: '5',
                    label: t('appointments_details.appointment_tab_5'),
                    style: { paddingBottom: 40 },
                    children: <Notes />
                  }
                ]
              : []),
            ...(hasPaymentReadPermission
              ? [
                  {
                    key: '6',
                    label: 'Payment information',
                    style: { paddingBottom: 40 },
                    children: <PaymentInfo />
                  }
                ]
              : []),
            {
              key: '7',
              label: 'State',
              style: { paddingBottom: 40 },
              children: <State />
            }
          ]}
        />
        <RejectModal />
        <DateModal />
        <StateModal />
      </PageWrapper>
    </AppointmentDetailContext.Provider>
  );
};

export default AppointmentsDetails;
